import { MAX_PRIORITY } from '@/constants'
import type { CharacteristicOptionType, CharacteristicSlotType } from '@/types'
import { trimTrailingZeros } from '@/utils/format/trimTrailingZeros'

/**
 * Утилиты для слотовых характеристик (`SLOTTED_REFERENCE`, например «Диаметр присоединения»).
 *
 * Значение опции фильтра приходит и уходит на бэкенд в формате `"<номер присоединения>:<значение>"`,
 * например `"1:32"`. Внутри одного присоединения значения объединяются по ИЛИ,
 * между разными присоединениями — по И.
 */

export const SLOTTED_CHARACTERISTIC_TYPE = 'SLOTTED_REFERENCE'

/** Разделитель между номером присоединения и значением в сыром значении опции. */
export const SLOT_VALUE_SEPARATOR = ':'
/** Разделитель присоединений в итоговой строке: `32 × 25 × 40 × 32`. */
export const SLOT_DISPLAY_SEPARATOR = ' × '
/** Разделитель нескольких значений внутри одного присоединения (ИЛИ). */
export const SLOT_MULTI_VALUE_SEPARATOR = '/'
/** Заглушка для присоединения, по которому ничего не выбрано. */
export const EMPTY_SLOT_PLACEHOLDER = '—'

export type ParsedSlotValue = {
  slotIndex: number
  value: string
}

export type SlotOption = {
  /** Сырое значение для запроса/URL, например `"1:32"`. */
  rawValue: string
  /** Значение без префикса присоединения, например `"32"`. */
  value: string
  count: number
  priority?: number
}

export type SlotGroup = {
  index: number
  options: SlotOption[]
}

export function isSlottedCharacteristicType(type?: string | null): boolean {
  return type === SLOTTED_CHARACTERISTIC_TYPE
}

/** Разбирает `"1:32"` в `{ slotIndex: 1, value: '32' }`. Возвращает null, если формат другой. */
export function parseSlotValue(raw: unknown): ParsedSlotValue | null {
  const match = /^(\d+):([\s\S]*)$/.exec(String(raw ?? ''))
  if (!match) return null

  const slotIndex = Number(match[1])
  if (!Number.isInteger(slotIndex) || slotIndex < 1) return null

  return { slotIndex, value: match[2] }
}

export function buildSlotValue(slotIndex: number, value: string): string {
  return `${slotIndex}${SLOT_VALUE_SEPARATOR}${value}`
}

export function isSlottedValue(raw: unknown): boolean {
  return parseSlotValue(raw) !== null
}

/**
 * Эвристика для случаев, когда тип характеристики недоступен (например, фильтры,
 * восстановленные из URL, приходят с типом STRING).
 */
export function looksLikeSlottedValues(values: string[]): boolean {
  return values.length > 0 && values.every(isSlottedValue)
}

/** Форматирует значение диаметра для показа (обрезает незначащие нули у чисел). */
export function formatSlotValue(value: string): string {
  const asNumber = Number(String(value).replace(',', '.'))
  if (String(value).trim() !== '' && !Number.isNaN(asNumber)) {
    return trimTrailingZeros(value)
  }
  return value
}

/**
 * Приоритет считается назначенным по тем же правилам, что и в остальных
 * фильтрах: конечное число больше нуля и меньше «бесконечности» MAX_PRIORITY.
 */
export function hasAssignedPriority(priority: unknown): boolean {
  const value = Number(priority)
  return Number.isFinite(value) && value > 0 && value < MAX_PRIORITY
}

/**
 * Порядок значений внутри присоединения.
 *
 * Приоритет, назначенный в настройках категории, важнее числового порядка:
 * значения с приоритетом идут первыми по возрастанию, остальные — следом
 * по возрастанию диаметра, текстовые (`1/2"`, `DN100`) в конце.
 *
 * Приоритет у слотовых характеристик приходит на опцию вида `"1:32"`,
 * то есть задаётся отдельно для каждого присоединения.
 */
function compareSlotOptions(a: SlotOption, b: SlotOption): number {
  const aHasPriority = hasAssignedPriority(a.priority)
  const bHasPriority = hasAssignedPriority(b.priority)

  if (aHasPriority && bHasPriority) {
    const diff = Number(a.priority) - Number(b.priority)
    if (diff !== 0) return diff
  } else if (aHasPriority !== bHasPriority) {
    return aHasPriority ? -1 : 1
  }

  const aNumber = Number(a.value.replace(',', '.'))
  const bNumber = Number(b.value.replace(',', '.'))
  const aIsNumber = a.value.trim() !== '' && !Number.isNaN(aNumber)
  const bIsNumber = b.value.trim() !== '' && !Number.isNaN(bNumber)

  if (aIsNumber && bIsNumber) return aNumber - bNumber
  if (aIsNumber) return -1
  if (bIsNumber) return 1
  return a.value.localeCompare(b.value, 'ru')
}

/**
 * Группирует плоский список опций фильтра (`1:32`, `1:40`, `2:25`, ...)
 * по номерам присоединений. Дополнительно добавляет уже выбранные значения,
 * которых нет в текущей фасетной выдаче, чтобы выбор не «пропадал» из UI.
 *
 * @param slotsCount количество присоединений категории из
 * `CharacteristicFilterGroup.slotsCount` (записка §2): список дополняется
 * пустыми слотами до этого числа, чтобы ряд кнопок был на каждое
 * присоединение, даже если в текущей выдаче по нему нет значений.
 */
export function groupSlotOptions(
  options: Pick<
    CharacteristicOptionType,
    'value' | 'count' | 'priority'
  >[] = [],
  selectedRawValues: string[] = [],
  slotsCount?: number | null,
  pinnedRawValues: string[] = []
): SlotGroup[] {
  const groups = new Map<number, Map<string, SlotOption>>()
  // Значения, не подходящие под формат `<присоединение>:<значение>` (записка §2).
  // Молча их терять нельзя: диаметр просто исчез бы из фильтра без следов.
  const skipped: string[] = []

  const put = (raw: string, count: number, priority?: number) => {
    const parsed = parseSlotValue(raw)
    if (!parsed) {
      if (String(raw ?? '').trim() !== '') skipped.push(String(raw))
      return
    }

    if (!groups.has(parsed.slotIndex)) groups.set(parsed.slotIndex, new Map())
    const slotOptions = groups.get(parsed.slotIndex)!

    const existing = slotOptions.get(parsed.value)
    if (existing) {
      existing.count = Math.max(existing.count, count)
      if (
        !hasAssignedPriority(existing.priority) &&
        hasAssignedPriority(priority)
      ) {
        existing.priority = priority
      }
      return
    }

    slotOptions.set(parsed.value, {
      rawValue: buildSlotValue(parsed.slotIndex, parsed.value),
      value: parsed.value,
      count,
      priority,
    })
  }

  options.forEach((option) =>
    put(String(option.value), Number(option.count ?? 0), option.priority)
  )
  selectedRawValues.forEach((raw) => put(String(raw), 0))
  pinnedRawValues.forEach((raw) => put(String(raw), 0))
  const pinned = new Set(
    pinnedRawValues.flatMap((raw) => {
      const parsed = parseSlotValue(raw)
      return parsed ? [buildSlotValue(parsed.slotIndex, parsed.value)] : []
    })
  )
  const comparePinnedFirst = (a: SlotOption, b: SlotOption) =>
    Number(pinned.has(b.rawValue)) - Number(pinned.has(a.rawValue)) ||
    compareSlotOptions(a, b)

  if (Number.isInteger(slotsCount) && (slotsCount as number) > 0) {
    for (let index = 1; index <= (slotsCount as number); index += 1) {
      if (!groups.has(index)) groups.set(index, new Map())
    }
  }

  if (skipped.length > 0) {
    console.warn(
      'Слотовая характеристика: значения не в формате "<присоединение>:<значение>" ' +
        '(записка §2) — пропущены и не показаны в фильтре.',
      skipped
    )
  }

  return Array.from(groups.entries())
    .sort(([a], [b]) => a - b)
    .map(([index, slotOptions]) => ({
      index,
      options: Array.from(slotOptions.values()).sort(comparePinnedFirst),
    }))
}

/**
 * Собирает итоговую строку выбора: `32 × 25 × 40 × 32`.
 * Присоединения без выбора отображаются как `—`, хвостовые пустые отбрасываются.
 */
export type SlottedDisplayValue = { raw: string; label: string }

export function groupSlottedValuesForDisplay(
  values: string[]
): SlottedDisplayValue[][] {
  const parsedValues = values.flatMap((raw) => {
    const parsed = parseSlotValue(raw)
    return parsed ? [{ raw, parsed }] : []
  })
  const maxSlotIndex = Math.max(
    0,
    ...parsedValues.map(({ parsed }) => parsed.slotIndex)
  )

  return Array.from({ length: maxSlotIndex }, (_, index) =>
    parsedValues
      .filter(({ parsed }) => parsed.slotIndex === index + 1)
      .map(({ raw, parsed }) => ({ raw, label: formatSlotValue(parsed.value) }))
  )
}

export function formatSlottedValues(values: string[]): string {
  const bySlot = new Map<number, string[]>()
  let maxSlotIndex = 0

  values.forEach((raw) => {
    const parsed = parseSlotValue(raw)
    if (!parsed) return

    maxSlotIndex = Math.max(maxSlotIndex, parsed.slotIndex)
    if (!bySlot.has(parsed.slotIndex)) bySlot.set(parsed.slotIndex, [])
    bySlot.get(parsed.slotIndex)!.push(formatSlotValue(parsed.value))
  })

  if (maxSlotIndex === 0) return ''

  const parts: string[] = []
  for (let index = 1; index <= maxSlotIndex; index += 1) {
    const slotValues = bySlot.get(index)
    parts.push(
      slotValues && slotValues.length > 0
        ? slotValues.join(SLOT_MULTI_VALUE_SEPARATOR)
        : EMPTY_SLOT_PLACEHOLDER
    )
  }

  return parts.join(SLOT_DISPLAY_SEPARATOR)
}

export function isUniformSlottedEligible(selectedRawValues: string[]): boolean {
  let firstSlotCount = 0
  let otherSlotsCount = 0

  new Set(selectedRawValues).forEach((raw) => {
    const parsed = parseSlotValue(raw)
    if (!parsed) return
    if (parsed.slotIndex === 1) firstSlotCount += 1
    else otherSlotsCount += 1
  })

  return firstSlotCount >= 1 && otherSlotsCount === 0
}

export function hasUniformEligibleSlottedFilter(
  characteristics: {
    options?: { value: unknown }[] | null
    chosenOptions?: string[] | null
  }[]
): boolean {
  return characteristics.some((characteristic) =>
    isUniformSlottedEligible([
      ...(characteristic.options?.map((option) => String(option.value)) ?? []),
      ...(characteristic.chosenOptions ?? []),
    ])
  )
}

/** Количество присоединений, по которым что-то выбрано. */
export function countSelectedSlots(values: string[]): number {
  const indexes = new Set<number>()
  values.forEach((raw) => {
    const parsed = parseSlotValue(raw)
    if (parsed) indexes.add(parsed.slotIndex)
  })
  return indexes.size
}

export function getSlotLabel(index: number): string {
  return `Присоединение ${index}`
}

/** Подпись значения опции для поиска/чипов: `1:32` → `32`. */
export function getSlotOptionSearchLabel(raw: string): string {
  const parsed = parseSlotValue(raw)
  return parsed ? formatSlotValue(parsed.value) : String(raw)
}

/* ------------------------------------------------------------------ *
 * Карточка товара: `slotsCount` + `slots` из NomenclatureCharacteristicModel
 * (разделы 1, 4 и 5 записки front_new_slotted_chrcs_docs.md)
 *
 * Контракт: `slotsCount` — жёсткий лимит инпутов, единственный источник
 * количества полей. `slots` — массив ТОЛЬКО фактически заполненных значений,
 * разреженный, сопоставляется по `index` (1-based). Плейсхолдеров с нулевыми
 * UUID бэкенд больше не присылает.
 * ------------------------------------------------------------------ */

/** `valueId` незаполненного присоединения (старый формат, оставлен как защита). */
export const EMPTY_SLOT_VALUE_ID = '00000000-0000-0000-0000-000000000000'

export function isEmptySlot(slot: CharacteristicSlotType): boolean {
  return (
    !slot.value ||
    slot.value.trim() === '' ||
    slot.valueId === EMPTY_SLOT_VALUE_ID
  )
}

/** Количество полей ввода: строго `slotsCount` (записка §1, ключевое правило). */
export function getSlotsCount(source: { slotsCount?: number | null }): number {
  const count = Number(source?.slotsCount)
  return Number.isInteger(count) && count > 0 ? count : 0
}

/**
 * Системный лимит присоединений, когда категория не задала `slotsCount`
 * (бэкенд присылает `null`). Записка «Значение по умолчанию для slotsCount»:
 * дефолтов (ни 1, ни 10) на бэке больше нет, при их отсутствии клиент рендерит
 * поля динамически, но не бесконечно — до этого потолка.
 */
export const DYNAMIC_SLOTS_LIMIT = 10

/**
 * Сколько полей ввода показать, когда лимит присоединений не задан:
 * все фактически заполненные присоединения плюс одно пустое для нового
 * значения, но не больше `DYNAMIC_SLOTS_LIMIT`.
 */
export function getDynamicSlotsCount(filledCount: number): number {
  const safeFilled = Number.isFinite(filledCount) ? Math.max(filledCount, 0) : 0
  return Math.min(safeFilled + 1, DYNAMIC_SLOTS_LIMIT)
}

/**
 * То же, но с подстраховкой по максимальному `index` в `slots`.
 *
 * Записка §1 обещает `slotsCount` всегда, но в swagger поле необязательное.
 * Если оно вдруг не придёт, без подстраховки количество полей вышло бы нулевым,
 * а при пересборке payload заполненная характеристика молча выпала бы из запроса —
 * то есть значения товара стёрлись бы. Берём максимум из двух источников.
 */
export function resolveSlotsCount(source: {
  slotsCount?: number | null
  slots?: CharacteristicSlotType[] | null
}): number {
  // Записка §1: slotsCount — единственный источник количества полей.
  // Если он пришёл, он и главный: слот с index больше лимита игнорируется,
  // иначе форма позволила бы собрать payload длиннее, чем разрешает категория
  // (characteristic.slotted.limit.exceeded).
  const declared = getSlotsCount(source)
  if (declared > 0) return declared

  return (source?.slots ?? []).reduce(
    (max, slot) => (isEmptySlot(slot) ? max : Math.max(max, slot.index)),
    0
  )
}

/**
 * Раскладывает разреженный `slots` в плотный массив длиной `slotsCount`,
 * сопоставляя по `index`. Незаполненные присоединения — пустые строки.
 *
 * @param pick какое поле слота брать: текст для показа или UUID для отправки
 */
function spreadSlotsByIndex(
  slots: CharacteristicSlotType[] | undefined | null,
  slotsCount: number,
  pick: (slot: CharacteristicSlotType) => string
): string[] {
  const result = new Array<string>(Math.max(slotsCount, 0)).fill('')

  ;(slots ?? []).forEach((slot) => {
    if (isEmptySlot(slot)) return
    const position = slot.index - 1
    if (position < 0 || position >= result.length) return
    result[position] = pick(slot) ?? ''
  })

  return result
}

/**
 * Текстовые значения для предзаполнения/показа, длиной `slotsCount`.
 * Записка §5 п.5: предзаполняем селекты, сопоставляя `index` слота
 * и порядковый номер селекта.
 */
export function slotsToFormValues(
  slots: CharacteristicSlotType[] | undefined | null,
  slotsCount: number
): string[] {
  return spreadSlotsByIndex(slots, slotsCount, (slot) => slot.value ?? '')
}

/**
 * UUID значений справочника, длиной `slotsCount`.
 * Записка §4a: при сохранении товара передаётся именно `valueId`,
 * а не текстовый литерал.
 */
export function slotsToFormValueIds(
  slots: CharacteristicSlotType[] | undefined | null,
  slotsCount: number
): string[] {
  return spreadSlotsByIndex(slots, slotsCount, (slot) => slot.valueId ?? '')
}

/**
 * Итоговая строка характеристики: `32 × 25 × 40 × 32` (записка §1).
 * Собирается из заполненных слотов по возрастанию `index`.
 */
export function formatSlotsValue(
  slots: CharacteristicSlotType[] | undefined | null
): string {
  return [...(slots ?? [])]
    .filter((slot) => !isEmptySlot(slot))
    .sort((a, b) => a.index - b.index)
    .map((slot) => formatSlotValue(slot.value))
    .join(SLOT_DISPLAY_SEPARATOR)
}

/** То же самое, но из готового массива значений (для превью при редактировании). */
export function formatSlotValuesList(values: (string | null)[]): string {
  return values
    .filter((value): value is string => !!value && value.trim() !== '')
    .map(formatSlotValue)
    .join(SLOT_DISPLAY_SEPARATOR)
}

/**
 * Индекс первого пропуска (1-based) или 0, если пропусков нет.
 * Записка §4a: заполнять последовательно от 1 до N, `["uuid-1", null, "uuid-2"]` —
 * ошибка `characteristic.slotted.gaps.not.allowed`.
 */
export function findSlotGap(values: (string | null)[]): number {
  const lastFilled = values.reduce(
    (acc, value, i) => (value && value.trim() !== '' ? i : acc),
    -1
  )
  for (let i = 0; i < lastFilled; i += 1) {
    const value = values[i]
    if (!value || value.trim() === '') return i + 1
  }
  return 0
}

/**
 * Готовит массив `values` для отправки: обрезает хвостовые пустые значения.
 * Возвращает `null`, если отправлять нечего или есть пропуск — бэкенд такое
 * отклонит (`characteristic.slotted.array.empty` /
 * `characteristic.slotted.gaps.not.allowed`), поэтому не отправляем вовсе.
 *
 * @param slotsCount если передан, лишние значения отсекаются, чтобы не поймать
 * `characteristic.slotted.limit.exceeded`
 */
export function buildSlotValuesPayload(
  values: (string | null)[],
  slotsCount?: number | null
): string[] | null {
  const limited =
    Number.isInteger(slotsCount) && (slotsCount as number) > 0
      ? values.slice(0, slotsCount as number)
      : [...values]

  if (findSlotGap(limited) !== 0) return null

  while (
    limited.length > 0 &&
    (!limited[limited.length - 1] ||
      String(limited[limited.length - 1]).trim() === '')
  ) {
    limited.pop()
  }

  if (limited.length === 0) return null
  return limited.map((value) => String(value))
}

/** Подпись поля ввода: «Диаметр присоединения 1» (записка §5 п.5). */
export function getSlotFieldLabel(
  characteristicName: string,
  index: number
): string {
  return `${characteristicName} ${index}`
}

/**
 * Схлопывает опции слотовой характеристики к уникальным «чистым» значениям.
 *
 * Нужно для экрана приоритетов: в `options` диаметр приходит по разу на каждое
 * присоединение (`1:32`, `2:32`, `3:32`), а `ValuePriorityUpdateDto.value` ждёт
 * значение без префикса слота — приоритет действует глобально, сразу во всех
 * присоединениях. Без схлопывания список дублировался бы, а на бэк уходила бы
 * строка `"1:32"`, которую он не сопоставит со справочником.
 */
export function collapseSlottedOptionsForPriority<
  T extends {
    value: string
    count?: number
    priority?: number
    isMain?: boolean
  },
>(options: T[]): T[] {
  const byValue = new Map<string, T>()

  options.forEach((option) => {
    const parsed = parseSlotValue(option.value)
    const bare = parsed ? parsed.value : String(option.value)
    const existing = byValue.get(bare)

    if (!existing) {
      byValue.set(bare, { ...option, value: bare })
      return
    }

    existing.count = Number(existing.count ?? 0) + Number(option.count ?? 0)
    if (
      !hasAssignedPriority(existing.priority) &&
      hasAssignedPriority(option.priority)
    ) {
      existing.priority = option.priority
    }
    if (option.isMain) existing.isMain = true
  })

  return Array.from(byValue.values())
}
